import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Selection } from 'react-aria-components';
import type { ButtonProps, PopoverProps } from '@launchpad-ui/components';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTrigger,
  Group,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';
import { debounce } from 'lodash';

import { Skeleton } from '../skeleton';

import styles from './SelectWithSearch.module.css';

export interface SelectOption {
  key: string;
  label: string;
}

export interface SelectWithSearchProps<T extends SelectOption = SelectOption> {
  options: T[];
  selectedKey: string | undefined;
  onSelectionChange: (key: string) => void;
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  prefix?: React.ReactNode;
  /** Number of options to show before showing the search input */
  searchThreshold?: number;
  disabled?: boolean;
  /** Custom render function for list items. Receives the item and returns a React node. */
  renderOption?: (item: T) => React.ReactNode;
  /** Custom render function for the selected value. Receives the selected item and returns a React node. */
  renderSelectValue?: (item: T) => React.ReactNode;
  emptyText?: React.ReactNode;
  buttonProps?: ButtonProps;
  popoverProps?: PopoverProps;
  /** When true, the popover width is pinned to match the trigger button width. */
  matchTriggerWidth?: boolean;
}

/** Select component that allows users to search and select an option from a list of options. */
export function SelectWithSearch<T extends SelectOption = SelectOption>({
  options,
  loading = false,
  selectedKey,
  onSelectionChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  ariaLabel = 'Select',
  prefix,
  searchThreshold = 10,
  disabled = false,
  renderOption,
  renderSelectValue,
  emptyText = 'No options available',
  buttonProps,
  popoverProps,
  matchTriggerWidth = false,
}: SelectWithSearchProps<T>) {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((option) => option.key === selectedKey);

  // Update filtered options when options change
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (!value.trim()) {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase())));
      }
    },
    [options],
  );

  const debouncedSearch = useMemo(() => debounce(handleSearch, 300), [handleSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel?.();
    };
  }, [debouncedSearch]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys !== 'all') {
      const key = Array.from(keys)[0];
      if (key) {
        onSelectionChange(key.toString());
        setIsOpen(false);
      }
    }
  };

  const shouldShowSearch = options.length >= searchThreshold;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open && matchTriggerWidth && buttonRef.current) {
        setPopoverWidth(buttonRef.current.offsetWidth);
      }
      if (!open) {
        // Cancel any pending debounced calls and reset search when popover closes
        debouncedSearch.cancel?.();
        setSearchValue('');
        setFilteredOptions(options);
      }
    },
    [debouncedSearch, matchTriggerWidth, options],
  );

  if (loading) {
    return <Skeleton width="100px" height="32px" />;
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button
        {...buttonProps}
        ref={buttonRef}
        className={clsx(styles.button, buttonProps?.className)}
        isDisabled={disabled}
        aria-label={ariaLabel}
      >
        {prefix ? <span className={styles.prefix}>{prefix}</span> : null}
        {selectedOption ? (
          <span className={styles.selectValue}>
            {renderSelectValue ? renderSelectValue(selectedOption) : selectedOption.label}
          </span>
        ) : options.length === 0 ? (
          <span className={styles.placeholder}>{emptyText}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover
        placement="bottom start"
        {...popoverProps}
        className={clsx(styles.popover, popoverProps?.className)}
        style={
          matchTriggerWidth && popoverWidth
            ? { minWidth: popoverWidth, maxWidth: `max(var(--lp-size-400), ${popoverWidth}px)`, ...popoverProps?.style }
            : popoverProps?.style
        }
      >
        <Dialog>
          <Autocomplete inputValue={searchValue} onInputChange={debouncedSearch}>
            {shouldShowSearch && (
              <SearchField autoFocus aria-label={`${ariaLabel} search`} className={styles.searchField}>
                <Group>
                  <Icon name="search" size="small" />
                  <Input placeholder={searchPlaceholder} />
                  <IconButton aria-label="clear search" icon="cancel-circle-outline" size="small" variant="minimal" />
                </Group>
              </SearchField>
            )}
            {filteredOptions.length === 0 ? (
              typeof emptyText === 'string' ? (
                <Text slot="subtitle" className={styles.emptyState}>
                  {emptyText}
                </Text>
              ) : (
                emptyText
              )
            ) : (
              <ListBox
                className={styles.listBox}
                items={filteredOptions}
                selectionMode="single"
                selectedKeys={selectedKey ? [selectedKey] : []}
                onSelectionChange={handleSelectionChange}
                shouldFocusWrap
              >
                {(item) => (
                  <ListBoxItem key={item.key} id={item.key} textValue={item.label}>
                    {renderOption ? renderOption(item) : item.label}
                  </ListBoxItem>
                )}
              </ListBox>
            )}
          </Autocomplete>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
